import get from 'lodash.get'

export const getAllocationString = (value, total) => {
  if (total === 0) return '0%'
  const percentage = (value / total) * 100
  return `${percentage.toFixed(2)}%`
}

// Simple hash function for creating deterministic IDs
const simpleHash = (str) => {
  let hash = 0
  if (str.length === 0) return hash.toString()
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(36)
}

// Counter for ensuring uniqueness when hash collisions occur
let idCounter = 0

export const generateItemId = (nameAccessor, item, depth = 0) => {
  // Try to use existing ID first
  if (item?.id) {
    return String(item.id)
  }

  // Get the name value, with fallback
  const nameValue = get(item, nameAccessor) || 'unnamed'

  // Create a deterministic hash based on item content
  const itemSignature = JSON.stringify({
    name: nameValue,
    value: get(item, 'value'),
    depth,
    // Include a few key properties to make the hash more unique
    ...(item.accountId && { accountId: item.accountId }),
    ...(item.color && { color: item.color })
  })

  const hash = simpleHash(itemSignature)

  // Increment counter for uniqueness (resets on each data processing cycle)
  idCounter++

  // Create a readable, deterministic ID
  return `${nameValue.replace(/[^a-zA-Z0-9]/g, '_')}-${hash}-${idCounter}`
}

// Reset ID counter (called at the start of data processing)
export const resetIdCounter = () => {
  idCounter = 0
}

export const getFormatString = (
  formats,
  depth,
  formatKey = 'blocks',
  fallback = '0.0a'
) => {
  const formatObj = formats?.[formatKey]
  if (typeof formatObj === 'string') {
    return formatObj
  }
  return formatObj?.[depth] || formatObj?.['0'] || fallback
}

export const getClassName = (classNames, depth, fallback = '') => {
  if (typeof classNames?.blocks === 'string') {
    return classNames.blocks
  }
  return classNames?.blocks?.[depth] || classNames?.blocks?.['0'] || fallback
}

export const getStyleByDepth = (styles, styleKey, depth) => {
  const styleObj = styles?.[styleKey] ?? {}
  return Array.isArray(styleObj)
    ? styleObj[depth] || styleObj[0] || {}
    : styleObj
}

export const getClassNameByDepth = (
  classNames,
  classKey,
  depth,
  fallback = ''
) => {
  const classObj = classNames?.[classKey]
  if (typeof classObj === 'string') {
    return classObj
  }
  return classObj?.[depth] || classObj?.['0'] || fallback
}

export const processValueFilter = (valueFilter, dataRow) => {
  const { op, value, accessor } = valueFilter
  const dataValue = get(dataRow, accessor)

  switch (op) {
    case 'eq':
      return dataValue === value
    case 'neq':
      return dataValue !== value
    case 'gt':
      return dataValue > value
    case 'gte':
      return dataValue >= value
    case 'lt':
      return dataValue < value
    case 'lte':
      return dataValue <= value
    case 'in':
      return Array.isArray(value) && value.includes(dataValue)
    case 'nin':
      return Array.isArray(value) && !value.includes(dataValue)
    case 'contains':
      return typeof dataValue === 'string' && dataValue.includes(value)
    case 'ncontains':
      return typeof dataValue === 'string' && !dataValue.includes(value)
    default:
      return false
  }
}

export const processDataRecursively = (
  data,
  {
    dataAccessor,
    nameAccessor,
    valueAccessor,
    colorAccessor,
    detailsAccessor,
    orderAccessor,
    orderDirection,
    separatedGroups,
    hideZeroValueAccounts,
    colors
  }
) => {
  if (!data) return []

  let currIndex = 0

  const sortItems = (a, b) => {
    const aValue = a.isSeparated
      ? -Infinity
      : get(a, orderAccessor) ?? -Infinity
    const bValue = b.isSeparated
      ? -Infinity
      : get(b, orderAccessor) ?? -Infinity

    return orderDirection === 'asc' ? aValue - bValue : bValue - aValue
  }

  const mapItemRecursively = (item, depth = 0) => {
    if (hideZeroValueAccounts && get(item, valueAccessor, 0) === 0) {
      return null
    }

    const colorIndex = currIndex % (colors?.length || 0)
    currIndex++

    const processedItem = {
      ...item,
      id: generateItemId(nameAccessor, item, depth),
      label: get(item, nameAccessor),
      value: get(item, valueAccessor),
      color:
        get(item, colorAccessor) || (colors ? colors[colorIndex] : '#eeeeee'),
      isSeparated: separatedGroups.some((sg) => processValueFilter(sg, item)),
      details: detailsAccessor
        ? get(item, detailsAccessor, [])
          .map((subItem) => mapItemRecursively(subItem, depth + 1))
          ?.filter(Boolean)?.sort(sortItems) ?? []
        : []
    }

    return processedItem
  }

  const items = get(data, dataAccessor, [])
  return items?.map((item) => mapItemRecursively(item, 0)).filter(Boolean)?.sort(sortItems) ?? []
}
